import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { FREE_LIMITS } from '../lib/stripe';
import { checkProEntitlement, isRevenueCatPlatform } from '../lib/revenueCat';

// Set to true to bypass all limits during testing
const TESTING_MODE = true;

export function useSubscription(userId) {
  const [isPro, setIsPro] = useState(TESTING_MODE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }

    async function check() {
      if (TESTING_MODE) { setLoading(false); return; }

      if (isRevenueCatPlatform()) {
        const entitled = await checkProEntitlement();
        setIsPro(entitled);
        setLoading(false);
        return;
      }
      const { data } = await supabase
        .from('user_settings')
        .select('subscription_status')
        .eq('user_id', userId)
        .maybeSingle();
      setIsPro(data?.subscription_status === 'active');
      setLoading(false);
    }

    check().catch(() => setLoading(false));
  }, [userId]);

  // Free users: max 5 clients total
  const checkClientLimit = useCallback(async () => {
    if (isPro) return null;
    const { count, error } = await supabase
      .from('clients')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId);
    if (error) return null;
    if (count >= FREE_LIMITS.clients) {
      return `Free plan includes ${FREE_LIMITS.clients} clients. Upgrade to Pro for unlimited clients.`;
    }
    return null;
  }, [isPro, userId]);

  // Free users: max 3 AI generations per calendar month
  const checkAiGenerationLimit = useCallback(async () => {
    if (isPro) return null;
    const currentMonth = new Date().toISOString().slice(0, 7); // 'YYYY-MM'
    const { data } = await supabase
      .from('user_settings')
      .select('ai_quotes_used, ai_quotes_reset_month')
      .eq('user_id', userId)
      .maybeSingle();
    // If month rolled over, treat count as 0
    const used = data?.ai_quotes_reset_month === currentMonth ? (data?.ai_quotes_used ?? 0) : 0;
    if (used >= FREE_LIMITS.aiGenerationsPerMonth) {
      return `You've used all ${FREE_LIMITS.aiGenerationsPerMonth} free AI generations this month. Upgrade to Pro for unlimited AI.`;
    }
    return null;
  }, [isPro, userId]);

  const incrementAiGenerations = useCallback(async () => {
    if (isPro) return;
    const currentMonth = new Date().toISOString().slice(0, 7);
    const { data } = await supabase
      .from('user_settings')
      .select('ai_quotes_used, ai_quotes_reset_month')
      .eq('user_id', userId)
      .maybeSingle();
    const isNewMonth = data?.ai_quotes_reset_month !== currentMonth;
    const current = isNewMonth ? 0 : (data?.ai_quotes_used ?? 0);
    await supabase
      .from('user_settings')
      .update({ ai_quotes_used: current + 1, ai_quotes_reset_month: currentMonth })
      .eq('user_id', userId);
  }, [isPro, userId]);

  // Free users: only 1 business allowed
  const checkMultiBusinessLimit = useCallback(async () => {
    if (isPro) return null;
    const { count, error } = await supabase
      .from('businesses')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId);
    if (error) return null;
    if (count >= 1) {
      return 'Multiple businesses is a Pro feature. Upgrade to manage more than one business.';
    }
    return null;
  }, [isPro, userId]);

  // Quotes are unlimited on all plans
  const checkQuoteLimit = useCallback(async () => {
    return null;
  }, []);

  // Free users cannot create invoices
  const checkInvoiceLimit = useCallback(() => {
    if (isPro) return null;
    return 'Invoicing is a Pro feature. Upgrade to create and send professional invoices.';
  }, [isPro]);

  return {
    isPro,
    loading,
    checkClientLimit,
    checkQuoteLimit,
    checkAiGenerationLimit,
    incrementAiGenerations,
    checkMultiBusinessLimit,
    checkInvoiceLimit,
  };
}
